import { motion } from 'framer-motion';
import { Printer, Award, Truck, Users, Zap, Shield } from 'lucide-react';

const stats = [
  { value: '+5', label: 'Anos no mercado' },
  { value: '+15k', label: 'Produtos entregues' },
  { value: '99%', label: 'Clientes satisfeitos' },
  { value: '+50k', label: 'Peças impressas' },
];

const values = [
  {
    icon: Award,
    title: 'Qualidade Premium',
    description: 'Utilizamos filamentos de alta qualidade e impressoras de precisão para garantir acabamento profissional em cada peça.',
  },
  {
    icon: Zap,
    title: 'Tecnologia de Ponta',
    description: 'Investimos constantemente em tecnologia 3D de última geração para oferecer os melhores resultados do mercado.',
  },
  {
    icon: Truck,
    title: 'Entrega Rápida',
    description: 'Produção ágil e envio para todo o Brasil. Seu produto impresso e entregue no menor tempo possível.',
  },
  {
    icon: Users,
    title: 'Atendimento Personalizado',
    description: 'Equipe especializada para ajudar em cada etapa, desde a ideia até a entrega do produto final.',
  },
  {
    icon: Shield,
    title: 'Garantia de Satisfação',
    description: 'Garantimos a qualidade de todos os nossos produtos. Se não estiver satisfeito, faremos de novo.',
  },
  {
    icon: Printer,
    title: 'Personalização Total',
    description: 'Cada produto pode ser personalizado conforme sua necessidade. Cores, tamanhos e acabamentos sob medida.',
  },
];

const team = [
  {
    name: 'Henrique',
    role: 'Fundador & CEO',
    description: 'Apaixonado por tecnologia 3D desde 2019.',
  },
];

export function Sobre() {
  return (
    <div className="min-h-screen bg-gray-950">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-cyan-500/5" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block px-4 py-2 rounded-full bg-orange-500/10 text-orange-400 text-sm font-medium mb-6">
              Nossa História
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              A casa do maker que{' '}
              <span className="text-orange-500">cria e inova</span>
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed">
              A Fort3D nasceu da paixão por tecnologia e da vontade de tornar a impressão 3D 
              acessível a todos. Somos uma equipe de makers, engenheiros e designers 
              trabalhando juntos para entregar produtos de alta qualidade.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl sm:text-5xl font-bold text-orange-500 mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-400">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Nossa Missão
              </h2>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  Transformar ideias em realidade através da impressão 3D de alta qualidade. 
                  Queremos democratizar o acesso à tecnologia 3D, tornando-a acessível para 
                  makers, empresas e entusiastas de todo o Brasil.
                </p>
                <p>
                  Acreditamos que cada pessoa tem uma ideia incrível esperando para ser 
                  materializada. Nossa missão é ser o parceiro que transforma essas ideias 
                  em objetos tangíveis, com qualidade profissional e preço justo.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square rounded-3xl bg-gradient-to-br from-gray-800 to-gray-900 border border-white/10 flex items-center justify-center">
                <div className="text-center p-8">
                  <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-gradient-to-br from-orange-500/20 to-cyan-500/20 flex items-center justify-center">
                    <Printer className="w-16 h-16 text-orange-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Fort3D</h3>
                  <p className="text-gray-400">Excelência em Impressão 3D</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Nossos Valores
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Princípios que guiam nosso trabalho diário e nos ajudam a entregar o melhor 
              para nossos clientes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-gray-800/50 border border-white/5 hover:border-orange-500/30 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-4">
                  <value.icon className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{value.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Nossa Equipe
            </h2>
            <p className="text-gray-400">
              Quem trabalha para tornar suas ideias realidade
            </p>
          </div>

          <div className="flex justify-center">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-gradient-to-br from-orange-500 to-cyan-500 flex items-center justify-center">
                  <span className="text-3xl font-bold text-white">
                    {member.name.charAt(0)}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white">{member.name}</h3>
                <p className="text-orange-500 font-medium">{member.role}</p>
                <p className="text-gray-400 text-sm mt-2">{member.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
